import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NotePreview = ({ 
  selectedNote, 
  onClose, 
  onEdit, 
  onCompleteDelivery, 
  onPartialDelivery 
}) => {
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    signedBy: '',
    notes: '',
    photo: null,
    gpsLocation: null
  });

  const [showPartialForm, setShowPartialForm] = useState(false);
  const [partialQuantities, setPartialQuantities] = useState({});

  if (!selectedNote) {
    return (
      <div className="bg-surface rounded-lg border border-border p-8">
        <div className="text-center">
          <Icon name="FileText" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-body-medium text-text-primary mb-2">
            Vista Previa de Albarán
          </h3>
          <p className="text-text-muted">
            Selecciona un albarán de la lista para ver su vista previa y gestionar la entrega
          </p>
        </div>
      </div>
    );
  }

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCompleteDelivery = (e) => {
    e.preventDefault();
    
    // Get GPS location if available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const gpsLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        onCompleteDelivery(selectedNote.id, {
          ...deliveryForm,
          gpsLocation
        });
      });
    } else {
      onCompleteDelivery(selectedNote.id, deliveryForm);
    }
    
    setShowDeliveryForm(false);
    setDeliveryForm({ signedBy: '', notes: '', photo: null, gpsLocation: null });
  };

  const handlePartialDelivery = (e) => {
    e.preventDefault();
    
    const deliveredQuantities = {};
    const remainingProducts = [];
    
    selectedNote.products.forEach(product => {
      const delivered = partialQuantities[product.sku] || 0;
      deliveredQuantities[product.sku] = delivered;
      
      const remaining = product.quantity - delivered;
      if (remaining > 0) {
        remainingProducts.push({
          ...product,
          quantity: remaining
        });
      }
    });
    
    onPartialDelivery(selectedNote.id, deliveredQuantities, remainingProducts);
    setShowPartialForm(false);
    setPartialQuantities({});
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDeliveryForm(prev => ({ ...prev, photo: file }));
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading-semibold text-text-primary">
            Albarán de Entrega
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Printer"
              onClick={() => window.print()}
            >
              Imprimir
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={onClose}
              />
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Document Header */}
        <div className="text-center border-b border-border pb-6">
          <h1 className="text-2xl font-heading-semibold text-text-primary mb-2">
            ALBARÁN DE ENTREGA
          </h1>
          <div className="text-lg font-data text-text-secondary">
            {selectedNote.id}
          </div>
          <div className="text-sm text-text-muted mt-1">
            Fecha de creación: {formatDateTime(selectedNote.createdDate)}
          </div>
        </div>

        {/* Company Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-body-medium text-text-primary mb-2">
              DATOS DE LA EMPRESA
            </h3>
            <div className="text-sm text-text-secondary space-y-1">
              <div className="font-body-medium">Everforce Manager</div>
              <div>CIF: B-12345678</div>
              <div>Calle Tecnología 123</div>
              <div>28001 Madrid, España</div>
              <div>Teléfono: +34 91 000 0000</div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-body-medium text-text-primary mb-2">
              DATOS DEL CLIENTE
            </h3>
            <div className="text-sm text-text-secondary space-y-1">
              <div className="font-body-medium">{selectedNote.customerName}</div>
              <div>{selectedNote.customerContact}</div>
              <div>{selectedNote.deliveryAddress?.street}</div>
              <div>
                {selectedNote.deliveryAddress?.postalCode} {selectedNote.deliveryAddress?.city}
              </div>
              <div>{selectedNote.deliveryAddress?.country}</div>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-secondary-50 rounded-lg">
          <div>
            <div className="text-xs text-text-muted">Pedido</div>
            <div className="text-sm font-body-medium text-text-primary">{selectedNote.orderId}</div>
          </div>
          <div>
            <div className="text-xs text-text-muted">Fecha de entrega</div>
            <div className="text-sm font-body-medium text-text-primary">
              {new Date(selectedNote.deliveryDate).toLocaleDateString('es-ES')}
            </div>
          </div>
          <div>
            <div className="text-xs text-text-muted">Conductor</div>
            <div className="text-sm font-body-medium text-text-primary">
              {selectedNote.driverName} ({selectedNote.vehiclePlate})
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div>
          <h3 className="text-sm font-body-medium text-text-primary mb-3">
            PRODUCTOS
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border border-border">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-body-medium text-text-secondary border-r border-border">
                    SKU
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-body-medium text-text-secondary border-r border-border">
                    Descripción
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-body-medium text-text-secondary border-r border-border">
                    Cantidad
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-body-medium text-text-secondary">
                    Entregado
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedNote.products?.map((product, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="px-4 py-2 text-sm text-text-secondary border-r border-border">
                      {product.sku}
                    </td>
                    <td className="px-4 py-2 text-sm text-text-primary border-r border-border">
                      {product.name}
                    </td>
                    <td className="px-4 py-2 text-sm text-text-secondary text-center border-r border-border">
                      {product.quantity}
                    </td>
                    <td className="px-4 py-2 text-sm text-text-primary text-center">
                      {product.delivered || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Instructions */}
        {selectedNote.instructions && (
          <div>
            <h3 className="text-sm font-body-medium text-text-primary mb-2">
              INSTRUCCIONES DE ENTREGA
            </h3>
            <div className="p-3 bg-secondary-50 rounded-lg text-sm text-text-secondary">
              {selectedNote.instructions}
            </div>
          </div>
        )}

        {/* Delivery Status */}
        {selectedNote.status === 'completed' && (
          <div className="p-4 bg-success-50 rounded-lg border border-success-200">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <h3 className="text-sm font-body-medium text-success">
                ENTREGA COMPLETADA
              </h3>
            </div>
            <div className="text-sm text-success space-y-1">
              <div>Entregado el: {formatDateTime(selectedNote.deliveryTime)}</div>
              <div>Firmado por: {selectedNote.signedBy}</div>
              {selectedNote.gpsLocation && (
                <div>
                  Ubicación: {selectedNote.gpsLocation.lat.toFixed(6)}, {selectedNote.gpsLocation.lng.toFixed(6)}
                </div>
              )}
              {selectedNote.notes && (
                <div>Observaciones: {selectedNote.notes}</div>
              )}
            </div>
          </div>
        )}

        {/* Signature Area */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-border">
          <div>
            <div className="text-xs text-text-muted mb-2">ENTREGA REALIZADA POR</div>
            <div className="h-16 border-b border-border mb-2"></div>
            <div className="text-xs text-text-muted">Firma del transportista</div>
          </div>
          <div>
            <div className="text-xs text-text-muted mb-2">ENTREGA RECIBIDA POR</div>
            <div className="h-16 border-b border-border mb-2"></div>
            <div className="text-xs text-text-muted">Firma del destinatario</div>
          </div>
        </div>

        {/* Action Buttons */}
        {selectedNote.status === 'pending' && !showDeliveryForm && !showPartialForm && (
          <div className="flex space-x-3 pt-4 border-t border-border">
            <Button
              onClick={() => setShowDeliveryForm(true)}
              className="flex-1"
              iconName="CheckCircle"
            >
              Confirmar Entrega Completa
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPartialForm(true)}
              iconName="Package"
            >
              Entrega Parcial
            </Button>
          </div>
        )}

        {/* Complete Delivery Form */}
        {showDeliveryForm && (
          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-body-medium text-text-primary mb-3">
              Confirmar Entrega Completa
            </h3>
            <form onSubmit={handleCompleteDelivery} className="space-y-4">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Firmado por *
                </label>
                <Input
                  type="text"
                  value={deliveryForm.signedBy}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, signedBy: e.target.value }))}
                  placeholder="Nombre de quien recibe"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Observaciones
                </label>
                <textarea
                  value={deliveryForm.notes}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Observaciones de la entrega..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Foto de entrega
                </label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary hover:file:bg-primary-100"
                />
              </div>

              <div className="flex space-x-3">
                <Button type="submit" className="flex-1">
                  Confirmar Entrega
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowDeliveryForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Partial Delivery Form */}
        {showPartialForm && (
          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-body-medium text-text-primary mb-3">
              Entrega Parcial
            </h3>
            <form onSubmit={handlePartialDelivery} className="space-y-4">
              <div className="space-y-3">
                {selectedNote.products?.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div>
                      <div className="text-sm font-body-medium text-text-primary">
                        {product.name}
                      </div>
                      <div className="text-xs text-text-muted">
                        Cantidad total: {product.quantity}
                      </div>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        value={partialQuantities[product.sku] || 0}
                        onChange={(e) => setPartialQuantities(prev => ({
                          ...prev,
                          [product.sku]: parseInt(e.target.value) || 0
                        }))}
                        min="0"
                        max={product.quantity}
                        placeholder="0"
                        className="text-center"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex space-x-3">
                <Button type="submit" className="flex-1">
                  Confirmar Entrega Parcial
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowPartialForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotePreview;